import React from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Grid } from '@opendoor/atoms/grid';
import { Text } from '@opendoor/atoms/text';
import { IconCircleCheckFilled, IconCircleXFilled, IconMinus } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { ICompareTable, ICompareTableRow } from 'declarations/contentful';

const SENTIMENT_LABELS: Record<string, string> = {
  positive: 'Advantage',
  negative: 'Disadvantage',
  neutral: 'Neutral',
};

const SentimentIcon: React.FC<{ sentiment: string }> = ({ sentiment }) => {
  let icon;
  if (sentiment === 'positive') {
    icon = (
      <IconCircleCheckFilled size={18} className="text-od-content-success" aria-hidden="true" />
    );
  } else if (sentiment === 'negative') {
    icon = <IconCircleXFilled size={18} className="text-od-content-error" aria-hidden="true" />;
  } else {
    icon = <IconMinus size={18} className="text-od-content-tertiary" aria-hidden="true" />;
  }

  return (
    <Box aria-label={SENTIMENT_LABELS[sentiment] || sentiment} className="shrink-0">
      {icon}
    </Box>
  );
};

const MobileCards: React.FC<{ rows: ICompareTableRow[]; competitorLabel: string }> = ({
  rows,
  competitorLabel,
}) => (
  <Col className="gap-2">
    {rows.map((row) => (
      <Col
        key={row.fields.category}
        className="gap-3 rounded-xl bg-od-bg-primary p-5 shadow-[0px_1px_2px_0px_rgba(20,35,58,0.04),0px_4px_16px_0px_rgba(20,35,58,0.04)]"
      >
        <Box className="border-b border-od-border-secondary pb-2">
          <Text as="span" className="text-label-sm font-medium text-od-content-tertiary">
            {row.fields.category}
          </Text>
        </Box>
        <Col className="gap-1">
          <Text as="span" className="text-label-sm text-od-content-tertiary">
            {competitorLabel}
          </Text>
          <Row className="items-start gap-2">
            <SentimentIcon sentiment={row.fields.competitorSentiment} />
            <Text as="span" className="text-body-sm text-od-content-secondary">
              {row.fields.competitorText}
            </Text>
          </Row>
        </Col>
        <Col className="gap-1">
          <Text as="span" className="text-label-sm text-od-content-accent-brand">
            Opendoor
          </Text>
          <Row className="items-start gap-2">
            <SentimentIcon sentiment={row.fields.opendoorSentiment} />
            <Text as="span" className="text-body-sm text-od-content-primary">
              {row.fields.opendoorText}
            </Text>
          </Row>
        </Col>
      </Col>
    ))}
  </Col>
);

const DesktopGrid: React.FC<{
  rows: ICompareTableRow[];
  competitorLabel: string;
  opendoorLabel: string;
}> = ({ rows, competitorLabel, opendoorLabel }) => (
  <Grid className="grid-cols-[20%_40%_40%] overflow-hidden rounded-2xl bg-od-bg-primary shadow-[0px_1px_2px_0px_rgba(20,35,58,0.04),0px_4px_16px_0px_rgba(20,35,58,0.04)]">
    {/* Header row */}
    <Box className="bg-od-bg-secondary p-4" />
    <Box className="bg-od-bg-secondary p-4">
      <Text as="span" className="text-label-sm font-medium text-od-content-tertiary">
        {competitorLabel}
      </Text>
    </Box>
    <Box className="border-l-4 border-l-od-content-accent-brand bg-od-bg-secondary p-4">
      <Text as="span" className="text-label-sm font-medium text-od-content-accent-brand">
        {opendoorLabel}
      </Text>
    </Box>

    {/* Data rows */}
    {rows.map((row) => (
      <React.Fragment key={row.fields.category}>
        <Box className="border-t border-od-border-secondary p-4">
          <Text as="span" className="text-label-sm font-medium text-od-content-secondary">
            {row.fields.category}
          </Text>
        </Box>
        <Box className="border-t border-od-border-secondary p-4">
          <Row className="items-start gap-2">
            <SentimentIcon sentiment={row.fields.competitorSentiment} />
            <Text as="span" className="text-body-sm text-od-content-secondary">
              {row.fields.competitorText}
            </Text>
          </Row>
        </Box>
        <Box className="border-t border-l-4 border-t-od-border-secondary border-l-od-content-accent-brand bg-od-bg-secondary p-4">
          <Row className="items-start gap-2">
            <SentimentIcon sentiment={row.fields.opendoorSentiment} />
            <Text as="span" className="text-body-sm text-od-content-primary">
              {row.fields.opendoorText}
            </Text>
          </Row>
        </Box>
      </React.Fragment>
    ))}
  </Grid>
);

const RenderCompareTable = (entry: ICompareTable) => {
  const { fields } = entry;

  return (
    <Row className="justify-center bg-od-bg-secondary py-20 max-md:py-12">
      <Box className="block w-full max-w-280 px-6">
        <Col className="gap-6">
          <Col className="gap-3">
            <Text as="p" className="text-label-sm font-medium text-od-content-accent-brand">
              {fields.eyebrow}
            </Text>
            <Text
              as="h2"
              className="text-subheader-lg text-od-content-primary max-md:text-subheader-md"
            >
              {fields.title}
            </Text>
            <Text as="p" className="max-w-145 text-body-lg text-od-content-secondary">
              {fields.description}
            </Text>
          </Col>
          <Box className="block md:hidden">
            <MobileCards rows={fields.rows} competitorLabel={fields.competitorLabel} />
          </Box>
          <Box className="hidden md:block">
            <DesktopGrid
              rows={fields.rows}
              competitorLabel={fields.competitorLabel}
              opendoorLabel={fields.opendoorLabel}
            />
          </Box>
        </Col>
      </Box>
    </Row>
  );
};

const CompareTable: EntryComponent<ICompareTable> = {
  render: RenderCompareTable,
};

export default CompareTable;
